import { Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';
import { CryptoUtils } from '../../shared/utils/crypto.utils';

declare let ga: Function;
declare let gtag: Function;

export interface WppOpenUserContext {
	// User information
	user_id: string; // Hashed email
	user_company?: string;
	user_country_code?: string;
	user_department?: string;
	user_job_role?: string;
	user_region?: string;

	// Workspace information
	workspace_market?: string;
	workspace_industry?: string;
	workspace_brand?: string;
	workspace_client?: string;
	workspace_region?: string;
	workspace_custom?: string;
	project?: string; // Project name from context

	// Context information
	context_type: string; // 'os' for iFramed apps
	tenant_name: string; // From tenant.name or empty string if not available

	// Product information
	product_name: string;
	product_category: string;
	product_subcategory?: string;

	// Application information
	application_id?: string; // From appInstance.devhubAppId
	application_instance_id?: string; // From appInstance.id
	product_type?: string; // From appInstance.devhubMetadata.productType
	nocode_apptype?: string; // From appInstance.devhubMetadata.noCodeAppType
}

@Injectable({
	providedIn: 'root'
})
export class AnalyticsService {
	private enabled: boolean;
	private gaType: 'UA' | 'GTM' | 'none' = 'none';
	private analyticsId: string;
	private userId: string;
	private sessionId: string;
	private deviceType: string;
	private businessId: string;
	private dimensions = {};
	private userContext: WppOpenUserContext | null = null;
	private previousUrl: string = '';
	private isInitialized: boolean = false;

	constructor() {
		// Default values
		const prodInfo = {
			product_name: 'CCE',
			product_category: 'Commerce Studio',
			product_subcategory: 'Content Creation'
		};

		this.userContext = {
			context_type: 'os',
			tenant_name: 'unknown',
			product_name: prodInfo.product_name || 'unknown',
			product_category: prodInfo.product_category || 'unknown',
			product_subcategory: prodInfo.product_subcategory || 'unknown',
			user_id: 'unknown',
			application_id: 'unknown',
			application_instance_id: 'unknown',
			product_type: 'unknown',
			nocode_apptype: 'unknown',
			workspace_market: undefined,
			workspace_industry: undefined,
			workspace_brand: undefined,
			workspace_region: undefined,
			workspace_custom: undefined,
			project: undefined
		};

		if (environment.analyticsMode === 'app') {
			if (!localStorage.getItem('GAID')) {
				localStorage.setItem('GAID', new Date().getTime() + '-' + Math.round(Math.random() * 10000));
			}
			this.userId = localStorage.getItem('GAID');
		}

		// Pull the tracking id from the index file.
		// tslint:disable-next-line: no-string-literal
		if (window['gaID'] !== 'none') {
			// tslint:disable-next-line: no-string-literal
			this.analyticsId = window['gaID'];
		}

		// Pull the tracking type from the index file.
		// tslint:disable-next-line: no-string-literal
		if (window['gaType'] !== 'none') {
			// tslint:disable-next-line: no-string-literal
			this.gaType = window['gaType'];
		}

		if (this.analyticsId !== 'none' && this.gaType !== 'none') {
			this.enabled = true;
		}
	}

	// Extract data from context to user context
	public async updateUserContext(context: any): Promise<void> {
		// Process user details
		if (context.userDetails) {
			const user = context.userDetails;
			const email = user.email || '';

			console.log('[GTM #2] User email processed');

			const hashedEmail = await CryptoUtils.hashEmail(email);

			this.userContext = {
				...this.userContext,
				user_id: hashedEmail,
				user_company: user.company || '',
				user_country_code: user.countryAlpha2Code || '',
				user_department: user.department || '',
				user_job_role: user.jobTitle || '',
				user_region: user.geoRegion || ''
			};
		}

		// Process workspace data
		if ((context as any).workspace?.mapping) {
			const workspaceMapping = (context as any).workspace.mapping;
			const workspaceTypes = ['MARKET', 'CLIENT', 'INDUSTRY', 'BRAND', 'REGION', 'CUSTOM'];
			const workspaceHierarchy: Record<string, any> = {};

			// Build workspace hierarchy
			let currentAzId = (context as any).workspace.azId;
			while (currentAzId && workspaceMapping[currentAzId]) {
				const workspace = workspaceMapping[currentAzId];
				workspaceHierarchy[workspace.type] = workspace;
				currentAzId = workspace.parentAzId;
			}

			// Map workspace types to context
			workspaceTypes.forEach(type => {
				const workspace = workspaceHierarchy[type];
				if (workspace) {
					switch (type) {
						case 'MARKET':
							this.userContext.workspace_market = workspace.name || '';
							break;
						case 'CLIENT':
							this.userContext.workspace_client = workspace.name || '';
							break;
						case 'INDUSTRY':
							this.userContext.workspace_industry = workspace.name || '';
							break;
						case 'BRAND':
							this.userContext.workspace_brand = workspace.name || '';
							break;
						case 'REGION':
							this.userContext.workspace_region = workspace.name || '';
							break;
						case 'CUSTOM':
							this.userContext.workspace_custom = workspace.name || '';
							break;
					}
				}
			});
		}

		// Set app info
		if (context.appInstance) {
			this.userContext = {
				...this.userContext,
				application_id: context.appInstance?.devhubAppId || '',
				application_instance_id: context.appInstance?.id || '',
				product_type: context.appInstance?.devhubMetadata?.productType || '',
				nocode_apptype: context.appInstance?.devhubMetadata?.noCodeAppType || ''
			};
		}

		// Set tenant name
		if (context.tenant?.name) {
			this.userContext = {
				...this.userContext,
				tenant_name: context.tenant.name
			};
		}

		// Set project name
		if (context.project?.name) {
			this.userContext = {
				...this.userContext,
				project: context.project.name
			};
		}
	}

	public setSessionVars(userId, deviceType?, businessId?) {
		if (!this.enabled) {
			return;
		}
		this.userId = userId;

		if (deviceType) {
			this.deviceType = deviceType;
		}

		if (businessId) {
			this.businessId = businessId;
		}
	}

	public setDimension(dimensionKey, dimensionValue) {
		if (!this.enabled) {
			return;
		}
		if (this.gaType === 'UA') {
			return;
		}

		// Create object to map that is just { dimension1: dimension1,... }
		this.dimensions = {
			...this.dimensions,
			dimensionKey
		};

		// Map all of those so they are being read in events
		if (typeof gtag === 'function') {
			gtag('config', 'GA_MEASUREMENT_ID', {
				custom_map: this.dimensions
			});
		}

		// Set that dimension using the same key we mapped
		const obj = {};
		obj[dimensionKey] = dimensionValue;

		window['dataLayer'].push(obj);
		// gtag('event', 'set_dimensions', obj);
	}

	public getUserId() {
		if (!this.enabled) {
			return;
		}
		return this.userId;
	}

	public startSession(forceNewSession?: boolean, userObject?: any): void {
		if (!this.enabled) {
			return;
		}
		if (!this.sessionId) {
			this.isInitialized = true;

			if (this.userId) {
				this.sessionId = this.userId;
			} else {
				this.sessionId = 'anonymous-user-' + Date.now().toString();
			}

			if (this.gaType === 'UA') {
				ga('set', 'userId', this.sessionId);
				ga('send', 'pageview', { sessionControl: 'start' });
			} else if (this.gaType === 'GTM') {
				if (typeof gtag === 'function') {
					gtag('config', 'GA_MEASUREMENT_ID', {
						user_id: this.sessionId
					});
				}
			}
		} else if (forceNewSession) {
			this.stopSession();
			this.startSession();
		}
	}

	public stopSession(): void {
		if (!this.enabled) {
			return;
		}
		if (this.sessionId) {
			if (this.gaType === 'UA') {
				ga('send', 'pageview', { sessionControl: 'end' });
				ga('set', 'userId', '');
			}
			this.sessionId = undefined;
		}
	}

	public sendEvent(category: string, action: string, label?: string, val?: number, extra?: any): void {
		if (!this.enabled) {
			return;
		}

		// console.log(`[GTM #10] Sending analytics event: ${category} - ${action}`);

		this.startSession();

		if (this.gaType === 'UA') {
			const obj: any = {
				eventCategory: category,
				eventAction: action,
				user: this.sessionId
			};

			if (label) {
				obj.eventLabel = label;
			}

			if (val) {
				obj.eventValue = val;
			}

			if (extra) {
				obj.extra = extra;
			}

			ga('send', 'event', obj);
		} else if (this.gaType === 'GTM') {
			let obj: any = {};
			if (window['wppopen_dataLayer'] === undefined) {
				obj = {
					event_category: category,
					user: this.sessionId
				};
			} else {
				obj = {
					event_category: category,
					...this.getUserContext()
				};
			}

			if (label) {
				obj.event_label = label;
			}

			if (val) {
				obj.value = val;
			}

			if (typeof gtag === 'function') {
				this.pushToDataLayer({
					event: action,
					...this.getUserContext()
				});

				gtag('event', action, obj);
			}
		}
	}

	public setPage(page: string, pageTitle?: string): void {
		if (!this.enabled) {
			return;
		}
		if (this.gaType === 'UA') {
			ga('set', 'page', page);
		} else if (this.gaType === 'GTM') {
			// Push to dataLayer
			this.pushToDataLayer({
				event: 'page_view',
				...this.getUserContext()
			});

			if (typeof gtag === 'function') {
				gtag('config', this.analyticsId, {
					page_title: pageTitle || 'undefined',
					page_path: page,
					send_page_view: true
				});
			}
		}
	}

	public sendPageView(page: string, pageTitle?: string): void {
		if (!this.enabled) {
			return;
		}
		this.startSession();
		if (this.gaType === 'UA') {
			ga('set', 'page', page);
			ga('send', {
				hitType: 'pageView',
				user: this.sessionId
			});
		} else if (this.gaType === 'GTM') {
			this.trackVirtualPageView(page, pageTitle || 'undefined');
			this.setPage(page, pageTitle);
		}
	}

	// Send to GTM datalayer
	private pushToDataLayer(data: any): void {
		// Skip if disabled
		// if (environment.analytics?.enabled === false) {
		// 	return;
		// }

		if (window['wppopen_dataLayer']) {
			window['wppopen_dataLayer'].push(data);
		} else {
			console.warn('wppopen_dataLayer not available');
		}
	}

	private getUserContext(): WppOpenUserContext {
		return this.userContext!;
	}

	// Track SPA navigation
	private trackVirtualPageView(pageUrl: string, pageTitle: string): void {
		// Require initialization
		if (!this.isInitialized) {
			console.warn('[GTM] Virtual page view skipped - not initialized');
			return;
		}

		this.pushToDataLayer({
			event: 'virtual_page_view',
			pageUrl,
			pageTitle,
			previousUrl: this.previousUrl,
			...this.getUserContext()
		});

		console.log('[GTM #11] Sent virtual_page_view event to data layer', {
			pageUrl,
			pageTitle,
			previousUrl: this.previousUrl
		});

		// Store for next navigation
		this.previousUrl = pageUrl;
	}
}
